import { breakOnBall, finishPlay, handoffPoint, planThrow, qbPosition, resolveCatch, startCatchRun, startRun, type LivePlay, type PlayOutcome } from './playEngine';
import { NO_INPUT, type RacWorld } from './rac';

/**
 * A bot that plays one down with no skill input: throws to the most open receiver at readAt seconds
 * (tap power, good catch timing) or runs the handoff, and lets the carrier run himself after.
 * Used by tests and balance checks; the game itself runs the same sim steps from player input.
 */
export function botPlay(live: LivePlay, readAt = 1.6): PlayOutcome {
  const { ctx, sim } = live;
  const t = ctx.t;
  const n = ctx.offense.length;
  const offPos = new Float32Array(n * 2);
  const defPos = new Float32Array(ctx.defense.length * 2);
  const ballOn = ctx.drive.ballOn;

  if (ctx.play.type === 'run') {
    const at = ctx.play.run?.toss ? t.run.tossSeconds : t.run.handoffSeconds;
    sim.positionsAt(at, offPos, defPos);
    const rbSlot = ctx.play.formation.slots.findIndex((s) => s.pos === 'RB');
    const p = handoffPoint(live, { x: 0, y: 0 });
    const m = ctx.flip ? -1 : 1;
    const aim = ctx.play.run ? { x: ctx.play.run.aim.x * m, y: ctx.play.run.aim.y } : null;
    const world = startRun(live, 'offense', rbSlot, p, { x: 0, y: 2 }, offPos, defPos, { aim, auto: true, blocking: true });
    return runToEnd(live, world, 'run', null, null, null, ballOn);
  }

  if (readAt >= live.sackTime) {
    const qb = qbPosition(live, live.sackTime, { x: 0, y: 0 });
    return finishPlay(live, { kind: 'pass', end: 'sack', gain: Math.round(qb.y), plan: null, pass: null, timing: null, events: [], carrier: ctx.offense[live.qbSlot]! });
  }
  let best = 0;
  sim.receivers.forEach((_, i) => {
    if (sim.separation(i, readAt) > sim.separation(best, readAt)) best = i;
  });
  const plan = planThrow(live, best, readAt);
  const pass = resolveCatch(live, plan, 'good');
  if (pass.outcome === 'interception') {
    return finishPlay(live, { kind: 'pass', end: 'interception', gain: 0, plan, pass, timing: 'good', events: [], carrier: null });
  }
  if (pass.outcome !== 'complete') {
    return finishPlay(live, { kind: 'pass', end: 'incomplete', gain: 0, plan, pass, timing: 'good', events: [], carrier: null });
  }
  const arrive = plan.throwTime + plan.flightSeconds;
  const atThrow = new Float32Array(defPos.length);
  sim.positionsAt(plan.throwTime, offPos, atThrow);
  sim.positionsAt(arrive, offPos, defPos);
  breakOnBall(live, plan, arrive, defPos, atThrow);
  const world = startCatchRun(live, plan, 'good', arrive, offPos, defPos, true);
  return runToEnd(live, world, 'pass', plan, pass, 'good', ballOn);
}

function runToEnd(
  live: LivePlay,
  world: RacWorld,
  kind: 'pass' | 'run',
  plan: PlayOutcome['plan'],
  pass: PlayOutcome['pass'],
  timing: PlayOutcome['timing'],
  ballOn: number,
): PlayOutcome {
  const step = live.ctx.t.run.fixedStepSeconds;
  while (!world.over) world.step(step, NO_INPUT, []);
  return outcomeFromRun(live, world, kind, plan, pass, timing, ballOn);
}

/** Turns a finished run into a play outcome. */
export function outcomeFromRun(
  live: LivePlay,
  world: RacWorld,
  kind: 'pass' | 'run',
  plan: PlayOutcome['plan'],
  pass: PlayOutcome['pass'],
  timing: PlayOutcome['timing'],
  ballOn: number,
): PlayOutcome {
  const last = world.log[world.log.length - 1];
  const end = last?.kind === 'touchdown' ? 'touchdown' : last?.kind === 'safety' ? 'safety' : last?.kind === 'outOfBounds' ? 'outOfBounds' : 'tackled';
  const gain = end === 'touchdown' ? 100 - ballOn : end === 'safety' ? -ballOn : Math.round(world.spotY);
  return finishPlay(live, { kind, end, gain, plan, pass, timing, events: world.log.filter((e) => e.kind !== 'move'), carrier: world.carrier.player });
}
